import React, { useEffect, useState } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, FormControl, Switch, TextField, MenuItem, Select, InputLabel, CircularProgress, DialogTitle, FormHelperText, Divider, Typography, IconButton, Tooltip, FormControlLabel, Alert } from '@mui/material';
import { styled } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoIcon from '@mui/icons-material/Info';
import httpclient from '../../Utils';

const StyledHeaderTitle = styled(DialogTitle)(({ theme }) => ({
    background: theme.palette.primary.main,
    color: "#fff",
}));


const CustomTooltip = styled(({ className, ...props }) => (
    <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
    '& .MuiTooltip-tooltip': {
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        color: theme.palette.common.white,
        fontSize: '1rem',
        padding: '16px',
        maxWidth: 450,
        lineHeight: 1.6,
        borderRadius: theme.shape.borderRadius,
    },
    '& .MuiTooltip-arrow': {
        color: 'rgba(0, 0, 0, 0.8)',
    },
}));

const EditAutomatedNotification = ({ open, onClose, sendEdit, isAddNew, viewDetails }) => {
    const userData = JSON.parse(localStorage.getItem("user"));

    const frequencyOptions = [
        { id: 'daily', name: "Every Day" },
        { id: 'weekly', name: "Every 1 Week" },
        { id: 'fortnightly', name: "Every 2 Week" },
        { id: 'monthly', name: "Every 30 Days" }
    ];

    const daysOfWeek = [
        { "id": "mon", "value": "Monday" },
        { "id": "tue", "value": "Tuesday" },
        { "id": "wed", "value": "Wednesday" },
        { "id": "thu", "value": "Thursday" },
        { "id": "fri", "value": "Friday" },
        { "id": "sat", "value": "Saturday" },
        { "id": "sun", "value": "Sunday" }
    ]


    const [dialogDetails, setDialogDetails] = useState({
        success: false,
    });
    const [validationErrors, setValidationErrors] = useState({});
    const [notificationTypes, setNotificationTypes] = useState([]);
    const [loadingTypes, setLoadingTypes] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [notificationTypeRules, setNotificationTypeRules] = useState([]);
    const [loadingRules, setLoadingRules] = useState(false);
    const [showRulesSection, setShowRulesSection] = useState(false);

    const [formData, setFormData] = useState({
        title: "",
        message: "",
        notify_type: "",
        notify_type_id: "",
        status: 0,
        created_by: userData?.user_name || "",
        updated_by: userData?.user_name || "",
        frequency: "",
        send_time: "",
        day: "",
        rules: [{ rule_type: "", value: "", message: "", status: 0, template: "", value_label: "" }]
    });

    const shouldShowFrequency = formData.notify_type === "Zont Miles";

    function formatToAMPM(time24) {
        if (!time24) return null;

        let [hours, minutes] = time24.split(':').map(Number);
        const ampm = hours >= 12 ? "PM" : "AM";

        hours = hours % 12 || 12;

        return `${hours}:${String(minutes).padStart(2, '0')} ${ampm}`;
    }

    function toOrdinal(num) {
        if (!num) return null;
        const suffixes = ["th", "st", "nd", "rd"];
        const v = num % 100;
        return num + (suffixes[(v - 20) % 10] || suffixes[v] || suffixes[0]);
    }
    

function getDayNameById(id) {
    const day = daysOfWeek.find(d => d.id === id);
    return day ? day.value : null;
}


    const getFrequencyMessage = () => {
        if (!formData.frequency) return "";

        switch (formData.frequency) {
            case 'daily':
                return `This notification will be sent every day at ${formatToAMPM(formData.send_time)}`;

            case 'weekly':
                return `This notification will be sent every week on ${getDayNameById(formData.day) || 'the selected day'} at ${formatToAMPM(formData.send_time)}`;

            case 'fortnightly':
                return `This notification will be sent every alternate week on ${getDayNameById(formData.day)|| 'the selected day'} at ${formatToAMPM(formData.send_time)}`;

            case 'monthly':
                return `This notification will be sent every month on the ${toOrdinal(formData.day) || 'selected date'} at ${formatToAMPM(formData.send_time)}`;

            default:
                return "";

        }
    };

    const formatRuleName = (ruleName) => {
        if (!ruleName) return "";
        return ruleName
            .split('_')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    };

    const getRuleDisplayName = (rule, index, rules) => {
        const baseName = formatRuleName(rule.rule_type);
        const sameNameRules = rules
            .map((r, i) => ({ rule: r, index: i }))
            .filter(item => item.rule.rule_type === rule.rule_type);

        if (sameNameRules.length <= 1) {
            return baseName;
        }

        const position = sameNameRules.findIndex(item => item.index === index);

        if (position === 0) {
            return `${baseName} (Option 1)`;
        } else if (position === 1) {
            return `${baseName} (Option 2)`;
        } else {
            return `${baseName} (${position + 1})`;
        }
    };

    useEffect(() => {
        const initialFormData = {
            title: viewDetails?.title || "",
            message: viewDetails?.message || "",
            notify_type: viewDetails?.notify_type || "",
            notify_type_id: viewDetails?.notify_type_id || "",
            status: viewDetails?.status !== undefined ? viewDetails.status : (isAddNew ? 0 : 1),
            [isAddNew ? "created_by" : "updated_by"]: userData?.user_name || "",
            frequency: viewDetails?.frequency || "",
            send_time: viewDetails?.send_time || "",
            day: viewDetails?.day || "",
            rules: viewDetails?.rules ?
                viewDetails.rules.map(rule => ({
                    ...rule,
                    status: rule.status !== undefined ? rule.status : 0,
                    value: rule.data_type === 'boolean' ? (rule.value !== undefined ? rule.value : rule.status) : rule.value,
                    template: rule.template || "",
                    value_label: rule.value_label || ""
                }))
                : [{ rule_type: "", value: "", message: "", status: 0, template: "", value_label: "" }]
        };

        setFormData(initialFormData);
        setValidationErrors({});

        if (!isAddNew && viewDetails?.rules && viewDetails.rules.length > 0) {
            setShowRulesSection(true);
            setNotificationTypeRules(viewDetails.rules);
        } else {
            setShowRulesSection(false);
            setNotificationTypeRules([]);
        }

        if (isAddNew) {
            fetchNotificationTypes();
        }
    }, [isAddNew, viewDetails, userData?.user_name]);

    const fetchNotificationTypes = async () => {
        setLoadingTypes(true);
        try {
            const response = await httpclient.get("request-response?requestName=lightspeed/notification/types");
            if (response.data?.data) {
                setNotificationTypes(response.data.data);
            }
        } catch (error) {
            console.error("Failed to fetch notification types", error);
        } finally {
            setLoadingTypes(false);
        }
    };

    const fetchNotificationTypeRules = async (notifyTypeId) => {
        if (!notifyTypeId) return;

        setLoadingRules(true);
        try {
            const response = await httpclient.get(`request-response?requestName=lightspeed/notification-type/rules/${notifyTypeId}`);
            if (response.data?.data) {
                setNotificationTypeRules(response.data.data);

                // Create rules from the fetched data with default status 0
                const newRules = response.data.data.map(rule => ({
                    rule_type: rule.rule_name,
                    value: rule.data_type === 'boolean' ? 0 : "", // For boolean, default to 0 (disabled)
                    message: "",
                    description: rule.description,
                    data_type: rule.data_type || "text",
                    status: 0,
                    template: rule.template || "", // Add template field
                    value_label: rule.value_label || "" // Add value_label field
                }));

                setFormData(prev => ({
                    ...prev,
                    rules: newRules.length > 0 ? newRules : [{ rule_type: "", value: "", message: "", status: 0, template: "", value_label: "" }]
                }));

                // Show rules section after fetching
                setShowRulesSection(true);
            }
        } catch (error) {
            console.error("Failed to fetch notification type rules", error);
            setNotificationTypeRules([]);
            setFormData(prev => ({
                ...prev,
                rules: [{ rule_type: "", value: "", message: "", status: 0, template: "", value_label: "" }]
            }));
            setShowRulesSection(true);
        } finally {
            setLoadingRules(false);
        }
    };

    const handleYes = async () => {
        let errors = {};
        if (!formData.title.trim()) errors.title = "Title is required.";
        if (isAddNew && !formData.notify_type_id) errors.notify_type_id = "Notification type is required.";
        if (shouldShowFrequency && !formData.frequency) errors.frequency = "Frequency is required.";
        if (!formData.send_time) errors.send_time = "Time is required.";

        // Validate frequency-specific fields
        if (shouldShowFrequency) {
            if ((formData.frequency === 'weekly' || formData.frequency === 'fortnightly' || formData.frequency === 'monthly') && !formData.day) {
                errors.day = "Day is required.";
            }
        }

        // Validate rules only if rules section is shown and rule is enabled
        if (showRulesSection) {
            const ruleErrors = [];
            formData.rules.forEach((rule, index) => {
                // Only validate if the rule is enabled
                if (rule.status === 1) {
                    if (!rule.rule_type.trim()) ruleErrors[index] = { ...ruleErrors[index], rule_type: "Rule type is required" };
                    // For non-boolean rules, validate the value
                    if (rule.data_type !== 'boolean' && !rule.value.toString().trim()) {
                        ruleErrors[index] = { ...ruleErrors[index], value: "Value is required" };
                    }
                    if (!rule.message.trim()) ruleErrors[index] = { ...ruleErrors[index], message: "Message is required" };
                }
            });
            if (ruleErrors.length > 0) errors.rules = ruleErrors;
        }

        if (Object.keys(errors).length > 0) {
            setValidationErrors(errors);
            return;
        }

        setIsSaving(true);
        try {
            const payload = {
                ...formData,
                frequency: shouldShowFrequency ? formData.frequency : 'daily',
                rules: formData.rules.map(rule => ({
                    ...rule,
                    value: rule.data_type === 'boolean' ? rule.status : rule.value
                }))
            };

            await sendEdit({ ...dialogDetails, success: true }, payload);
            setDialogDetails({ success: true });
            setIsSaving(false);
            onClose();
        } catch (error) {
            console.error("Failed to save notification:", error);
            setValidationErrors({
                api: error?.response?.data?.message || "Failed to save notification. Please try again.",
            });
            setIsSaving(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prevData) => ({
            ...prevData,
            [name]: value,
        }));
        validateField(name, value);
    };

    const validateField = (name, value) => {
        let errors = { ...validationErrors };
        if (name === "title" && !value.trim()) {
            errors.title = "Title is required.";
        } else if (name === "title") {
            delete errors.title;
        }
        if (name === "notify_type_id" && !value && isAddNew) {
            errors.notify_type_id = "Notification type is required.";
        } else if (name === "notify_type_id") {
            delete errors.notify_type_id;
        }
        if (name === "frequency" && !value && shouldShowFrequency) {
            errors.frequency = "Frequency is required.";
        } else if (name === "frequency") {
            delete errors.frequency;
        }
        if (name === "send_time" && !value) {
            errors.send_time = "Time is required.";
        } else if (name === "send_time") {
            delete errors.send_time;
        }
        if (name === "day" && (formData.frequency === 'weekly' || formData.frequency === 'fortnightly' || formData.frequency === 'monthly') && !value) {
            errors.day = "Day is required.";
        } else if (name === "day") {
            delete errors.day;
        }
        setValidationErrors(errors);
    };

    const handleNotificationTypeChange = (e) => {
        const selectedTypeId = e.target.value;
        const selectedType = notificationTypes.find(type => type.id === selectedTypeId);

        // Reset frequency and related fields when changing notification type
        const updatedFormData = {
            ...formData,
            notify_type_id: selectedTypeId,
            notify_type: selectedType ? selectedType.name : '',
            frequency: "", // Reset frequency when type changes
            day: ""
        };

        setFormData(updatedFormData);
        validateField("notify_type_id", selectedTypeId);

        // Fetch rules for the selected notification type
        if (selectedTypeId) {
            fetchNotificationTypeRules(selectedTypeId);
        } else {
            setShowRulesSection(false);
            setNotificationTypeRules([]);
        }
    };

    const handleRuleChange = (index, field, value) => {
        // Skip rule_type changes when editing (isAddNew is false) or when using predefined rules
        if (field === 'rule_type' && (!isAddNew || notificationTypeRules.length > 0)) {
            return;
        }

        const newRules = [...formData.rules];
        newRules[index][field] = value;

        // For boolean rules, when status changes, update value to match status
        if (field === 'status' && newRules[index].data_type === 'boolean') {
            newRules[index].value = value;
        }

        setFormData({ ...formData, rules: newRules });

        // Clear validation errors when field is changed
        if (validationErrors.rules && validationErrors.rules[index] && validationErrors.rules[index][field]) {
            const newErrors = { ...validationErrors };
            delete newErrors.rules[index][field];
            if (Object.keys(newErrors.rules[index]).length === 0) {
                delete newErrors.rules[index];
            }
            if (Object.keys(newErrors.rules).length === 0) {
                delete newErrors.rules;
            }
            setValidationErrors(newErrors);
        }
    };

    const handleAddRule = () => {
        // Only allow adding new rules if we're in add mode and no predefined rules exist
        if (isAddNew && notificationTypeRules.length === 0) {
            setFormData({
                ...formData,
                rules: [...formData.rules, { rule_type: "", value: "", message: "", status: 0, template: "", value_label: "" }]
            });
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
        >
            <StyledHeaderTitle id="alert-dialog-title">
                {isAddNew ? "Add Automated Notification" : "Edit Automated Notification"}
            </StyledHeaderTitle>
            <DialogContent>
                <Box pt={3}>
                    <Box p={3} sx={{ boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)" }}>
                        <h5 style={{ margin: 0 }}><i>{"Fields with * are mandatory"}</i></h5>
                        <Box display="flex" flexDirection="column" gap={2}>
                            {isAddNew ? (
                                <FormControl fullWidth margin="normal" error={!!validationErrors.notify_type_id}>
                                    <InputLabel id="notification-type-label">Notification Type *</InputLabel>
                                    <Select
                                        labelId="notification-type-label"
                                        id="notify_type_id"
                                        name="notify_type_id"
                                        value={formData.notify_type_id}
                                        onChange={handleNotificationTypeChange}
                                        label="Notification Type *"
                                        disabled={loadingTypes}
                                    >
                                        {loadingTypes ? (
                                            <MenuItem value="">
                                                <CircularProgress size={20} /> Loading...
                                            </MenuItem>
                                        ) : (
                                            notificationTypes.map((type) => (
                                                <MenuItem key={type.id} value={type.id}>
                                                    {type.name}
                                                </MenuItem>
                                            ))
                                        )}
                                    </Select>
                                    {validationErrors.notify_type_id && (
                                        <div className="error-text">{validationErrors.notify_type_id}</div>
                                    )}
                                </FormControl>
                            ) : (
                                <TextField
                                    label="Notification Type"
                                    name="notification_type"
                                    value={formData.notify_type}
                                    fullWidth
                                    margin="normal"
                                    disabled
                                    InputProps={{
                                        readOnly: true,
                                    }}
                                />
                            )}
                            <TextField
                                required
                                label="Title"
                                name="title"
                                value={formData.title}
                                onChange={handleChange}
                                fullWidth
                                margin="normal"
                                error={!!validationErrors.title}
                                helperText={validationErrors.title}
                            />

                            {/* Conditionally render frequency field - only show for Months Inactivity */}
                            {shouldShowFrequency && (
                                <Box display="flex" gap={2} alignItems="flex-start">
                                    <FormControl fullWidth margin="normal" required error={!!validationErrors.frequency}>
                                        <InputLabel id="frequency-label">Frequency</InputLabel>
                                        <Select
                                            labelId="frequency-label"
                                            id="frequency"
                                            name="frequency"
                                            value={formData.frequency}
                                            onChange={handleChange}
                                            label="Frequency"
                                        >
                                            {frequencyOptions.map(option => (
                                                <MenuItem key={option.id} value={option.id}>
                                                    {option.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        {validationErrors.frequency && <FormHelperText>{validationErrors.frequency}</FormHelperText>}
                                    </FormControl>

                                    {/* Day selector for weekly and fortnightly */}
                                    {(formData.frequency === 'weekly' || formData.frequency === 'fortnightly') && (
                                        <FormControl fullWidth margin="normal" required error={!!validationErrors.day}>
                                            <InputLabel id="day-label">Day of the Week</InputLabel>
                                            <Select
                                                labelId="day-label"
                                                id="day"
                                                name="day"
                                                value={formData.day}
                                                onChange={handleChange}
                                                label="Day of the Week"
                                            >
                                                {daysOfWeek.map(day => (
                                                    <MenuItem key={day} value={day.id}>
                                                        {day.value}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                            {validationErrors.day && <FormHelperText>{validationErrors.day}</FormHelperText>}
                                        </FormControl>
                                    )}

                                    {/* Day selector for monthly */}
                                    {formData.frequency === 'monthly' && (
                                        <FormControl fullWidth margin="normal" required error={!!validationErrors.day}>
                                            <InputLabel id="day-label">Day of the Month</InputLabel>
                                            <Select
                                                labelId="day-label"
                                                id="day"
                                                name="day"
                                                value={formData.day}
                                                onChange={handleChange}
                                                label="Day of the Month"
                                            >
                                                {Array.from({ length: 28 }, (_, i) => i + 1).map(day => (
                                                    <MenuItem key={day} value={day}>
                                                        {day}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                            {validationErrors.day && <FormHelperText>{validationErrors.day}</FormHelperText>}
                                        </FormControl>
                                    )}
                                </Box>
                            )}

                            <TextField
                                required
                                label="Notification Schedule Time"
                                name="send_time"
                                type="time"
                                value={formData.send_time}
                                onChange={handleChange}
                                fullWidth
                                margin="normal"
                                InputLabelProps={{
                                    shrink: true,
                                }}
                                error={!!validationErrors.send_time}
                                helperText={validationErrors.send_time}
                            />

                            {/* Info message for frequency - only show after frequency is selected */}
                            {formData.frequency && formData.send_time && (
                                <Alert severity="info" sx={{ mb: 2 }}>
                                    {getFrequencyMessage()}
                                </Alert>
                            )}

                            {showRulesSection && (
                                <>
                                    <Divider sx={{ my: 2 }} />
                                    <Typography variant="h6" gutterBottom>
                                        Notification Rules
                                    </Typography>

                                    {formData.notify_type === "Birthday Message" && (
                                        <Alert severity="info" sx={{ mb: 2 }}>
                                            You have two notification options for both before and after the customer's birthday. You can enable any of them as needed, or disable the others if you prefer to send fewer or single notifications.
                                        </Alert>
                                    )}

                                    {loadingRules ? (
                                        <Box display="flex" justifyContent="center" my={2}>
                                            <CircularProgress />
                                        </Box>
                                    ) : (
                                        formData.rules.map((rule, index) => {
                                            const displayName = getRuleDisplayName(rule, index, formData.rules);

                                            return (
                                                <Box key={index} sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                                                    {/* Rule header with name and switch */}
                                                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                                                        <Box display="flex" alignItems="center">
                                                            <Typography variant="subtitle1" fontWeight="bold">
                                                                {displayName}
                                                            </Typography>
                                                            {notificationTypeRules.length > 0 && (
                                                                <CustomTooltip
                                                                    title={
                                                                        <Box>
                                                                            <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                                                                                Rule Description
                                                                            </Typography>
                                                                            <Typography variant="body1">
                                                                                {rule.description || "No description available"}
                                                                            </Typography>
                                                                        </Box>
                                                                    }
                                                                    placement="top"
                                                                    arrow
                                                                >
                                                                    <IconButton color="info" size="large">
                                                                        <InfoIcon fontSize="medium" />
                                                                    </IconButton>
                                                                </CustomTooltip>
                                                            )}
                                                        </Box>

                                                        <FormControlLabel
                                                            control={
                                                                <Switch
                                                                    checked={rule.status === 1}
                                                                    onChange={(e) => handleRuleChange(index, 'status', e.target.checked ? 1 : 0)}
                                                                    name={`status_${index}`}
                                                                    color="primary"
                                                                />
                                                            }
                                                            label={rule.status === 1 ? "Enabled" : "Disabled"}
                                                        />
                                                    </Box>

                                                    {rule.status === 1 && (
                                                        <Box display="flex" flexDirection="column" gap={2}>
                                                            <Box display="flex" gap={2} alignItems="center">
                                                                {rule.data_type !== 'boolean' && (
                                                                    <>
                                                                        {rule.data_type === 'number' ? (
                                                                            <TextField
                                                                                required
                                                                                label={rule.value_label || "Value"}
                                                                                name={`value_${index}`}
                                                                                type="number"
                                                                                value={rule.value}
                                                                                onChange={(e) => handleRuleChange(index, 'value', e.target.value)}
                                                                                fullWidth={false}
                                                                                sx={{ width: '190px' }}
                                                                                margin="normal"
                                                                                error={!!(validationErrors.rules?.[index]?.value)}
                                                                                helperText={validationErrors.rules?.[index]?.value}
                                                                            />
                                                                        ) : (
                                                                            <TextField
                                                                                required
                                                                                label={rule.value_label || "Value"}
                                                                                name={`value_${index}`}
                                                                                value={rule.value}
                                                                                onChange={(e) => handleRuleChange(index, 'value', e.target.value)}
                                                                                fullWidth={false}
                                                                                sx={{ width: '190px' }}
                                                                                margin="normal"
                                                                                error={!!(validationErrors.rules?.[index]?.value)}
                                                                                helperText={validationErrors.rules?.[index]?.value}
                                                                            />
                                                                        )}
                                                                    </>
                                                                )}

                                                                <TextField
                                                                    required
                                                                    label="Message"
                                                                    name={`message_${index}`}
                                                                    value={rule.message}
                                                                    onChange={(e) => handleRuleChange(index, 'message', e.target.value)}
                                                                    fullWidth
                                                                    margin="normal"
                                                                    sx={{ minWidth: '270px' }}
                                                                    error={!!(validationErrors.rules?.[index]?.message)}
                                                                    helperText={validationErrors.rules?.[index]?.message}
                                                                />
                                                            </Box>

                                                            {/* Show template if available */}
                                                            {rule.template && (
                                                                <Box mt={1} sx={{ backgroundColor: '#f5f5f5', p: 2, borderRadius: 1 }}>
                                                                    <Typography variant="body2" color="textSecondary">
                                                                        <strong>Message Template:</strong> {rule.template} <br />
                                                                        {rule.rule_type == 'min_zont_mile_for_notification' && (
                                                                            <>
                                                                                <strong>Info:</strong> {"## will be replaced with actual value of points while sending notification"}
                                                                            </>
                                                                        )}
                                                                    </Typography>
                                                                </Box>
                                                            )}
                                                        </Box>
                                                    )}
                                                </Box>
                                            );
                                        })
                                    )}

                                    {isAddNew && notificationTypeRules.length === 0 && (
                                        <Button
                                            variant="outlined"
                                            startIcon={<AddIcon />}
                                            onClick={handleAddRule}
                                            sx={{ alignSelf: 'flex-start', mt: 1 }}
                                        >
                                            Add New Rule
                                        </Button>
                                    )}
                                </>
                            )}

                            <FormControl fullWidth>
                                <Box display="flex" alignItems="center">
                                    <Switch
                                        checked={formData.status === 1}
                                        onChange={(e) =>
                                            setFormData((prev) => ({
                                                ...prev,
                                                status: e.target.checked ? 1 : 0,
                                            }))
                                        }
                                    />
                                    <span>{formData.status === 1 ? "Active" : "Inactive"}</span>
                                </Box>
                            </FormControl>
                        </Box>
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions sx={{ margin: "5px 10px" }}>
                <Button
                    onClick={onClose}
                    color="error"
                    variant="contained"
                    disabled={isSaving}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleYes}
                    color="primary"
                    variant="contained"
                    disabled={isSaving}
                >
                    {isSaving ? (
                        <>
                            <CircularProgress size={20} sx={{ mr: 1, color: "white" }} />
                            {isAddNew ? "Saving..." : "Updating..."}
                        </>
                    ) : (
                        isAddNew ? "Save" : "Update"
                    )}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default EditAutomatedNotification;